import { Button } from '@/components/Button'
import { MultiSelect } from '@/components/MultiSelect'
import { ErrorMsg } from '@/components/ErrorMsg'
import { useToast } from '@/hooks/Toast'
import { useUser } from '@/hooks/useUser'
import { useOrganizationMembers } from '@/hooks/useOrganizationMembers'
import { useAddTeamMember } from '@/hooks/useAddTeamMember'
import { useForm, ValidationRules } from '@/hooks/useForm'

import s from './AddTeamMember.module.css'

type Props = {
  teamId: string
  onClose: () => void
}

type FormValues = {
  members: {
    label: string
    value: string
  }[]
}

const validationRules: ValidationRules = {
  members: {
    minLength: 1,
    minLengthMsg: 'Please select member(s)',
  },
}

export const AddTeamMember = ({ teamId, onClose }: Props) => {
  const { user } = useUser()

  const { data: orgMembers = [] } = useOrganizationMembers({
    organizationId: user?.organizationId,
    excludeTeamId: teamId,
  })

  const { values, errors, setFieldValue, isSubmitting, handleSubmit } = useForm(
    {
      initialValues: { members: [] } as FormValues,
      validationRules: validationRules,
    }
  )

  const addMemberMutation = useAddTeamMember()
  const toast = useToast()

  const onSubmit = async () => {
    try {
      await addMemberMutation.mutateAsync({
        teamId,
        userIds: values.members.map((v) => v.value),
      })
      onClose()
    } catch {
      toast.error('Something went wrong. Try again?')
    }
  }

  return (
    <>
      <form className={s.form} onSubmit={handleSubmit(() => onSubmit())}>
        <div className={s.field}>
          <MultiSelect
            items={orgMembers.map((m) => ({
              label: m.email,
              value: m.userId,
            }))}
            placeholder="Select Members"
            selectedTextFn={(selectedItems) =>
              selectedItems.length === 0
                ? 'Select Members'
                : selectedItems.length === 1
                ? `${selectedItems[0].label}`
                : selectedItems.length === orgMembers?.length
                ? 'All members selected'
                : `${selectedItems.length} members selected`
            }
            onChange={(value) => setFieldValue('members', value)}
          />
          {errors.members && <ErrorMsg text={errors.members} matter={false} />}
        </div>

        <Button variant="primary" type="submit" disabled={isSubmitting}>
          Add Member{values.members.length !== 1 ? 's' : ''}
        </Button>
      </form>
    </>
  )
}
